import { useEffect, useState } from 'react';
import { DirectionsRunRounded, AspectRatioRounded, FastRewind, FastForward } from '@mui/icons-material';
import {
  Box,
  FilledInput,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import styles from './styles';
import useAspectRatio from '../../../../hooks/useAspectRatio';
import { useDispatch, useSelector } from 'react-redux';
import { CALENDER_PROPS } from '../../../../utils/constants';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import { listFieldPicturesDrill } from '../../../../store/actions/fieldPicturesDrill';

const FieldPicturesDrillControls = () => {
  const dispatch = useDispatch();
  const { preferences } = useSelector((state) => state.currentUser);
  const { loading } = useSelector((state) => state.fieldPicturesDrill);
  const classes = styles();
  const [selectedStartDateTime, setSelectedStartDateTime] = useState(null);
  const [selectedEndDateTime, setSelectedEnDateTime] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);

  const handleAspectRatio = useAspectRatio();

  const handleStartTime = (date) => {
    setSelectedStartDateTime(date);
  };

  const handleEndTime = (date) => {
    setSelectedEnDateTime(date);
  };

  const handleSearch = () => {
    const body = {
      searchValue: searchTerm,
      startDate: selectedStartDateTime,
      endDate: selectedEndDateTime
    };
    dispatch({
      type: REDUX_ACTIONS.FIELD_PICTURES_DRILL_SEARCH_CONTROL,
      payload: body,
    });
    dispatch({
      type: REDUX_ACTIONS.FIELD_PICTURES_DRILL_SET_CURRENT_PAGE,
      payload: 1
    });
    dispatch(listFieldPicturesDrill(body));
  };

  useEffect(() => {
    const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const end = new Date();
    const body = {
      startDate: start,
      endDate: end
    };
    setSelectedStartDateTime(start);
    setSelectedEnDateTime(end);
    dispatch(listFieldPicturesDrill(body));
  }, []);

  const calculateTimeDiff = (start, end) => {
    return Math.abs(new Date(end).getTime() - new Date(start).getTime());
  };

  const handleTimeShift = (type) => {
    let updatedStartDateTime = selectedStartDateTime ? new Date(selectedStartDateTime) : null;
    let updatedEndDateTime = selectedEndDateTime ? new Date(selectedEndDateTime) : null;

    if (updatedStartDateTime && updatedEndDateTime) {
      const timeDiff = calculateTimeDiff(updatedStartDateTime, updatedEndDateTime);

      if (type === 'increase') {
        updatedStartDateTime = new Date(updatedStartDateTime.getTime() + timeDiff);
        updatedEndDateTime = new Date(updatedEndDateTime.getTime() + timeDiff);

        if (updatedStartDateTime > new Date()) {
          return;
        }
      } else {
        updatedStartDateTime = new Date(updatedStartDateTime.getTime() - timeDiff);
        updatedEndDateTime = new Date(updatedEndDateTime.getTime() - timeDiff);
      }

      setSelectedStartDateTime(updatedStartDateTime);
      setSelectedEnDateTime(updatedEndDateTime);
    }
  };

  const isForwardDisabled = () => {
    if (selectedStartDateTime && selectedEndDateTime) {
      const timeDiff = calculateTimeDiff(selectedStartDateTime, selectedEndDateTime);
      const futureStartDate = new Date(selectedStartDateTime.getTime() + timeDiff);
      return futureStartDate > new Date();
    }
    return false;
  };

  return (
    <Grid alignItems="flex-end" justifyContent="flex-end" sx={{ marginLeft: 'auto' }}>
      <Box className={classes.container}>
        <Box className={classes.searchContainer}>
          <FormControl>
            <InputLabel
              htmlFor="outlined-adornment-search"
              inputprops={{ classes: { root: classes.searchBar } }}
            >
              Search
            </InputLabel>
            <FilledInput
              className={classes.searchBar}
              id="outlined-adornment-search"
              type="text"
              label="Search"
              value={searchTerm || ''}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FormControl>
        </Box>
        <Box className={classes.dateSwiftContainer}>
          <IconButton onClick={() => handleTimeShift('decrease')} disabled={loading}>
            <FastRewind />
          </IconButton>
          <IconButton onClick={() => handleTimeShift('increase')} disabled={loading || isForwardDisabled()}>
            <FastForward />
          </IconButton>
        </Box>
        <Box className={classes.dateContainer}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            {...CALENDER_PROPS}
            label="Start Date"
            value={selectedStartDateTime}
            onChange={handleStartTime}
            className={classes.dateTimePicker}
            classes={{
              switchHeader: classes.datePickerHeader,
            }}
            disableFuture
            textField={(params) => <TextField {...params} />}
          />
          <DateTimePicker
            {...CALENDER_PROPS}
            label="End Date"
            value={selectedEndDateTime}
            onChange={handleEndTime}
            className={classes.dateTimePicker}
            disabled={!selectedStartDateTime}
            disableFuture
            minDate={selectedStartDateTime}
            textField={(params) => <TextField {...params} />}
          />
        </LocalizationProvider>
        </Box>
        <Box className={classes.runActionContainer}>
        <IconButton
            size="large"
            aria-label="orders"
            onClick={handleSearch}
          >
            <DirectionsRunRounded />
          </IconButton>
        </Box>
        <Box className={classes.aspectRatioContainer}>
          <IconButton
            edge="end"
            onClick={() => {
              handleAspectRatio(!preferences.aspectChanged);
            }}
          >
            <AspectRatioRounded />
          </IconButton>
        </Box>
      </Box>
    </Grid>
  );
};

export default FieldPicturesDrillControls;
